import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField, Typography } from '@mui/material';
import FuseDialog from '@fuse/core/FuseDialog';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import DIALOGS from 'app/constants/dialogs';
import { TRANSLATION_KEY, MINIMUM_DEPOSIT, MAXIMUM_DEPOSIT } from '../constants';
import { formatCurrencyAmount } from '../currency';

const RefillBalanceDialog = ({ currency, onConfirm }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_KEY);
  const [amount, setAmount] = useState('');

  const onClose = useCallback(() => {
    dispatch(closeDialog());
    setTimeout(() => setAmount(''), 300);
  }, [dispatch]);

  const isBelowMinimum = amount !== '' && Number(amount) < MINIMUM_DEPOSIT;
  const isAboveMaximum = amount !== '' && Number(amount) > MAXIMUM_DEPOSIT;
  const isValid = amount !== '' && !isBelowMinimum && !isAboveMaximum;

  const errorText = isBelowMinimum
    ? t('REFILL_MIN_AMOUNT', { amount: formatCurrencyAmount(MINIMUM_DEPOSIT, currency) })
    : isAboveMaximum
    ? t('REFILL_MAX_AMOUNT', { amount: formatCurrencyAmount(MAXIMUM_DEPOSIT, currency) })
    : '';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onClose();
    onConfirm(amount);
  };

  return (
    <FuseDialog
      fullWidth
      maxWidth="xs"
      dialogId={DIALOGS.REFILL_BALANCE}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: '400px' } }}
    >
      <Box padding="32px" component="form" onSubmit={handleSubmit}>
        <Typography variant="h6" fontWeight={700} color="accent.900">
          {t('REFILL_BALANCE_TITLE')}
        </Typography>
        <Typography variant="body2" color="accent.500" mt="4px">
          {t('REFILL_BALANCE_DESCRIPTION')}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          type="text"
          value={amount}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d+\.?\d{0,2}$/.test(val)) setAmount(val);
          }}
          placeholder={t('REFILL_AMOUNT_PLACEHOLDER')}
          inputProps={{ min: MINIMUM_DEPOSIT, max: MAXIMUM_DEPOSIT, step: '0.01' }}
          error={isBelowMinimum || isAboveMaximum}
          helperText={errorText}
          sx={{ mt: '20px' }}
        />

        <Box display="flex" gap="16px" mt="24px">
          <Button onClick={onClose} sx={{ textTransform: 'none', color: 'accent.900' }}>
            {t('CANCEL')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            disabled={!isValid}
            fullWidth
            sx={{ borderRadius: '24px', textTransform: 'none' }}
          >
            {t('REFILL_CONFIRM')}
          </Button>
        </Box>
      </Box>
    </FuseDialog>
  );
};

export default RefillBalanceDialog;
